import { useMemo } from 'react'

import { useGetMarketplaceProducts } from 'components/hooks/useGetMarketplaceProducts'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { MarketplaceCards } from 'slab'

import { Product } from './Product'
import { type SelectedCategory } from '../shared/types'
import { isProductNew } from '../shared/utils'

const MAX_NEW_PRODUCTS = 3

export const MarketplaceProducts = ({ activeCategories }: { activeCategories: SelectedCategory[] }) => {
  const { data: products, error, isLoading, isSuccess } = useGetMarketplaceProducts()

  const displayProducts = useMemo(() => {
    if (!products) return []

    const filtered = products.data.filter((product) =>
      activeCategories.length === 0 || product.categories.some((c) => c !== 'All' && activeCategories.includes(c))
    )

    const newProducts = filtered
      .filter((p) => isProductNew(p.most_recent_variant_created_at))
      .sort((a, b) => a.rank - b.rank)

    const topNew = newProducts.slice(0, MAX_NEW_PRODUCTS)
    const topNewIds = new Set(topNew.map((p) => p.id))

    const remaining = filtered
      .filter((p) => !topNewIds.has(p.id))
      .sort((a, b) => a.rank - b.rank)

    return [...topNew, ...remaining]
  }, [products, activeCategories])

  if (isLoading && error === null) {
    return (
      <MarketplaceCards>
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonItem key={index} heightClass='h-80' />
        ))}
      </MarketplaceCards>
    )
  }

  if (error) {
    // TODO(yousef): add better error representation
    return <div>Error: {error.message}</div>
  }

  return (
    <MarketplaceCards>
      {isSuccess &&
        displayProducts.map((product, index) => (
          <Product
            key={product.id}
            product={product}
            position={index + 1}
            activeCategories={activeCategories}
          />
        ))}
    </MarketplaceCards>
  )
}
